import type { VariantProps } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { FOCUS_RING } from '@/lib/focus-ring'
import { cn } from '@/lib/utils'
import { cva } from 'class-variance-authority'

const badgeVariants = cva(
  // shrink-0 because the base sets `overflow-hidden`: without it a badge in a
  // flex row shrinks below its own text and hard-clips the label ("Accepted"
  // rendered as "Accepte"). Badges are short status text, so the row's other
  // children (titles, descriptions) are what should truncate. `max-w-full`
  // still caps the badge at the parent width, and text wraps rather than
  // clipping in that case. Call sites can opt back into shrinking by passing
  // `shrink` in className — twMerge lets it win.
  [
    'inline-flex max-w-full shrink-0 items-center overflow-hidden rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
    // A badge is focusable only as a trigger; ring it for keyboard use only.
    FOCUS_RING,
  ],
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary:
          'border-transparent bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
        destructive: 'border-transparent bg-red-500/90 text-white',
        outline:
          'border-slate-200 bg-transparent text-slate-600 dark:border-slate-700 dark:text-slate-400',
        custom: 'border shadow-sm',
        success: 'border-transparent bg-emerald-500/90 text-white',
        warning: 'border-transparent bg-amber-500/90 text-white',
        info: 'border-transparent bg-sky-500/90 text-white',
        purple: 'border-transparent bg-violet-500/90 text-white',
        glass:
          'border border-slate-200/50 bg-white text-slate-600 dark:border-slate-700/50 dark:bg-slate-900 dark:text-slate-400',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

export interface BadgeProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
